import { CheckOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { TablePaginationConfig, TableProps } from 'antd'
import { Button, Card, DatePicker, Input, message, Select, Table, Tooltip } from 'antd'
import copy from 'copy-to-clipboard'
import dayjs from 'dayjs'
import { AllocationItemV2 } from 'Modules'
import { useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ExportCSVV2 from 'component/dropdown/export-csvv2'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { useDebounce } from 'hooks/use-debounce'
import AllocationDropdownCell from 'modules/account-vesting-v2/allocation-dropdown-cell'

import 'pages/list-email/style.scss'

interface EmailData {
  index: number
  allocation: number
  allocationHMC: number
  allocationHMCV2: AllocationItemV2[]
  allocationVentures: number
  allocationVenturesV2: AllocationItemV2[]
  email: string
  hashEmail: string
  isSign: number
  isVerify: number
  walletAddress: string
  updatedAt: string
  _id: string
}

interface ListEmailV2Params {
  page: number
  size: number
  walletAddress?: string
  email?: string
  hashEmail?: string
  dateFrom?: number
  dateTo?: number
  isSign?: number
  isVerify?: number
}

const fetchListEmailV2 = async (params: ListEmailV2Params) => {
  const res = await AppAdmin.getListEmailV2({
    size: params.size,
    page: params.page,
    walletAddress: params.walletAddress || undefined,
    email: params.email || undefined,
    hashEmail: params.hashEmail || undefined,
    // isSign/isVerify can be 0, so only drop them when undefined (not falsy)
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    isSign: params.isSign,
    isVerify: params.isVerify,
  })
  const offset = (params.page - 1) * params.size
  const results: EmailData[] = (res.data?.data ?? []).map((item: any, index: number) => ({
    index: offset + index + 1,
    allocation: item?.allocation,
    allocationHMC: item?.allocationHMC,
    allocationHMCV2: item?.allocationHMCV2 ?? [],
    allocationVentures: item?.allocationVentures,
    allocationVenturesV2: item?.allocationVenturesV2 ?? [],
    email: item?.email,
    hashEmail: item?.hashEmail,
    isSign: item?.isSign,
    isVerify: item?.isVerify,
    walletAddress: item?.walletAddress,
    updatedAt: item?.updatedAt,
    _id: item._id,
  }))
  return { results, total: res.data?.totalCount ?? 0 }
}

const ListEmailV2 = () => {
  const additionalItems = [BREADCRUMBS.EMAIL_V2]

  // What the user is typing
  const [email, setEmail] = useState('')
  const [walletAddress, setWalletAddress] = useState('')
  const [hashEmail, setHashEmail] = useState('')

  const debouncedEmail = useDebounce(email, 500)
  const debouncedWalletAddress = useDebounce(walletAddress, 500)
  const debouncedHashEmail = useDebounce(hashEmail, 500)

  // Single source of truth for the request: page + committed filters in one object.
  const [params, setParams] = useState<ListEmailV2Params>({
    page: 1,
    size: 10,
    email: '',
    walletAddress: '',
    hashEmail: '',
  })

  // Commit a (debounced) filter and jump back to page 1 in one update.
  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      email: debouncedEmail,
      walletAddress: debouncedWalletAddress,
      hashEmail: debouncedHashEmail,
    }))
  }, [debouncedEmail, debouncedWalletAddress, debouncedHashEmail])

  // Discrete filters (date range / selects) apply immediately and reset to page 1.
  const applyFilter = (patch: Partial<ListEmailV2Params>) => {
    setParams((prev) => ({ ...prev, ...patch, page: 1 }))
  }

  const { data, isFetching } = useQuery({
    queryKey: ['listEmailV2', params],
    queryFn: () => fetchListEmailV2(params),
    placeholderData: keepPreviousData,
  })

  const emails = data?.results ?? []
  const totalCount = data?.total ?? 0

  const columns: TableProps<EmailData>['columns'] = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
    },
    {
      title: 'User Email',
      dataIndex: 'hashEmail',
      key: 'hashEmail',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>{text}</div>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'User Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (text) =>
        text ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <a target="_blank" rel="noreferrer" href={`https://basescan.org/address/${text}`}>
              {text?.slice(0, 6)}... {text?.slice(-6)}
            </a>
            <Tooltip title="Copy">
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => {
                  copy(text)
                  message.success('Copied Success!')
                }}
              />
            </Tooltip>
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'Allocation HMC V2',
      dataIndex: 'allocationHMCV2',
      key: 'allocationHMCV2',
      render: (_: AllocationItemV2[], record: EmailData) => (
        <AllocationDropdownCell
          items={record.allocationHMCV2}
          totalDisplay={record.allocationHMCV2.reduce((sum, item) => sum + (item.value || 0), 0)}
        />
      ),
    },
    {
      title: 'Allocation Ventures V2',
      dataIndex: 'allocationVenturesV2',
      key: 'allocationVenturesV2',
      render: (_: AllocationItemV2[], record: EmailData) => (
        <AllocationDropdownCell
          items={record.allocationVenturesV2}
          totalDisplay={record.allocationVenturesV2.reduce(
            (sum, item) => sum + (item.value || 0),
            0
          )}
        />
      ),
    },
    {
      title: 'Verify Status',
      dataIndex: 'isVerify',
      key: 'isVerify',
      render: (text) => (
        <>
          {text === 0 ? (
            <div className="check">
              <CloseOutlined />
            </div>
          ) : (
            <div className="checked">
              <CheckOutlined />
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Confirm Wallet Status',
      dataIndex: 'isSign',
      key: 'isSign',
      render: (text) => (
        <>
          {text === 0 ? (
            <div className="check">
              <CloseOutlined />
            </div>
          ) : (
            <div className="checked">
              <CheckOutlined />
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
    },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setParams((prev) => ({
      ...prev,
      page: pagination.current || 1,
      size: pagination.pageSize || 10,
    }))
  }

  return (
    <div className="main-section user">
      <Breadcrumb additionalItems={additionalItems} />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            marginBottom: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <Input
            allowClear
            placeholder="Wallet Address"
            style={{ width: 200 }}
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
          />
          <Input
            allowClear
            placeholder="Email"
            style={{ width: 200 }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            allowClear
            placeholder="Hash Email"
            style={{ width: 200 }}
            value={hashEmail}
            onChange={(e) => setHashEmail(e.target.value)}
          />
          <Select
            placeholder="Verify Status"
            allowClear
            style={{ width: 160 }}
            value={params.isVerify}
            onChange={(value) => applyFilter({ isVerify: value ?? undefined })}
            options={[
              { label: 'Verified', value: 1 },
              { label: 'Not Verified', value: 0 },
            ]}
          />
          <Select
            placeholder="Confirm Wallet"
            allowClear
            style={{ width: 160 }}
            value={params.isSign}
            onChange={(value) => applyFilter({ isSign: value ?? undefined })}
            options={[
              { label: 'Confirmed', value: 1 },
              { label: 'Not Confirmed', value: 0 },
            ]}
          />
          <DatePicker
            showTime
            placeholder="Sign Date From"
            style={{ width: 190 }}
            value={params.dateFrom ? dayjs.unix(params.dateFrom) : null}
            onChange={(date) => applyFilter({ dateFrom: date ? date.unix() : undefined })}
          />
          <DatePicker
            showTime
            placeholder="Sign Date To"
            style={{ width: 190 }}
            value={params.dateTo ? dayjs.unix(params.dateTo) : null}
            onChange={(date) => applyFilter({ dateTo: date ? date.unix() : undefined })}
          />
        </div>
        <ExportCSVV2
          filters={{
            walletAddress: params.walletAddress || undefined,
            email: params.email || undefined,
            hashEmail: params.hashEmail || undefined,
            dateFrom: params.dateFrom,
            dateTo: params.dateTo,
            isSign: params.isSign,
            isVerify: params.isVerify,
          }}
        />
      </div>
      <Card style={{ margin: '0 auto' }}>
        <Table<EmailData>
          rowKey="_id"
          columns={columns}
          dataSource={emails}
          loading={isFetching}
          pagination={
            totalCount < 11
              ? false
              : { total: totalCount, current: params.page, pageSize: params.size }
          }
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          bordered
        />
      </Card>
    </div>
  )
}

export default ListEmailV2
